/**
 * The app's ONE QueryClient, and the defaults that make it.
 *
 * WHY THIS FILE EXISTS. These defaults used to live inline in `App.tsx`, which
 * meant no test could reach them: `App` mounts the entire router, so nothing in
 * the suite ever constructed it, and the two settings that matter most were
 * pinned by nothing at all. T-20260826-1 turned both of them into behaviour
 * (`retry` refuses 429; `refetchOnWindowFocus` stopped bypassing `staleTime`),
 * so they need a seam a test can hold. `queryClient.test.ts` is that guard —
 * revert either line and a test fails naming the invariant.
 *
 * `App.tsx` consumes `makeQueryClient()` and constructs nothing itself; the
 * guard asserts that too, because exporting the defaults would otherwise be
 * decorative if the app quietly kept its own copy.
 */

import { QueryClient, type DefaultOptions } from '@tanstack/react-query';

import { shouldRetryQuery } from './retryPolicy';

export const QUERY_CLIENT_DEFAULT_OPTIONS: DefaultOptions = {
  queries: {
    // PU-5.10 AC-2 — mobile optimisation: fewer network calls on slow
    // connections, instant display from cache.
    staleTime: 5 * 60 * 1000, // 5 minutes (was 30 seconds)
    // Keep cached data alive across navigation.
    gcTime: 10 * 60 * 1000, // 10 minutes
    // T-20260826-1: a bare `retry: 1` DOUBLED the request count during a
    // rate-limit event — the one error class that must never be retried
    // blindly. The predicate refuses 429 and keeps the single retry otherwise.
    retry: shouldRetryQuery,
    // T-20260826-1: `'always'` bypassed `staleTime` entirely, so every tab
    // switch refired every mounted query. `true` lets `staleTime` gate it.
    refetchOnWindowFocus: true,
    // Don't refetch on reconnect for mobile — let `staleTime` handle it.
    refetchOnReconnect: false,
  },
};

/** The single client `App` mounts. A factory, so tests can build their own. */
export function makeQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: QUERY_CLIENT_DEFAULT_OPTIONS });
}
